import React, { useState, useEffect } from "react";
import {
  ContentLayout,
  Container,
  Header,
  Table,
  Box,
  SpaceBetween,
  Button,
  Modal,
  FormField,
  Input,
  Textarea,
  Toggle,
  Spinner,
  Alert,
  StatusIndicator,
  TextFilter,
  Pagination,
  Badge,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import {
  getRegexPatterns,
  createRegexPattern,
  updateRegexPattern,
  deleteRegexPattern,
} from "../api/api";
import { useAlert } from "../components/Alert";

function RegexPatterns({ setAlert }) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlert();

  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filteringText, setFilteringText] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const PAGE_SIZE = 10;

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  const [showTestExtractionModal, setShowTestExtractionModal] = useState(false);
  const [testExtractionFilename, setTestExtractionFilename] = useState(
    "llamada_SUP_Maria_BC_SantaFe_CT_OUT_GUID_12345.mp3"
  );
  const [extractionResults, setExtractionResults] = useState([]);
  const [isTestingAllPatterns, setIsTestingAllPatterns] = useState(false);

  const [selectedPattern, setSelectedPattern] = useState(null);
  const [newPattern, setNewPattern] = useState({
    id: "",
    name: "",
    regex: "",
    description: "",
    active: true,
    lastModified: new Date().toISOString(),
  });
  const [formErrors, setFormErrors] = useState({});

  const [testFilename, setTestFilename] = useState(
    "llamada_SUP_Maria_BC_SantaFe_CT_OUT_GUID_12345.mp3"
  );
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const fetchPatterns = async () => {
    try {
      setLoading(true);
      const response = await getRegexPatterns();
      const normalizedPatterns = (response || []).map((pattern) => ({
        ...pattern,
        active: pattern.active === true || pattern.active === "true",
      }));

      setPatterns(normalizedPatterns);
      setFilteredItems(normalizedPatterns);
    } catch (error) {
      console.error("Error obteniendo patrones regex:", error);
      showError(t("regex.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatterns();
  }, []);

  useEffect(() => {
    if (filteringText) {
      const lowerFilteringText = filteringText.toLowerCase();
      const filtered = patterns.filter(
        (item) =>
          item.id.toLowerCase().includes(lowerFilteringText) ||
          item.regex.toLowerCase().includes(lowerFilteringText) ||
          item.description.toLowerCase().includes(lowerFilteringText)
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(patterns);
    }
    setCurrentPageIndex(1);
  }, [filteringText, patterns]);

  const pagesCount = Math.ceil(filteredItems.length / PAGE_SIZE);
  const startIndex = (currentPageIndex - 1) * PAGE_SIZE;
  const paginatedItems = filteredItems.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  const validateForm = (formData) => {
    const errors = {};

    if (formData === newPattern && !formData.name?.trim()) {
      errors.name = t("regex.requiredField");
    }

    if (formData.id) {
      const exists = patterns.some(
        (p) =>
          p.id === formData.id && (showAddModal || p.id !== selectedPattern?.id)
      );
      if (exists) {
        errors.name = t("regex.duplicateName");
      }
    }

    if (!formData.regex?.trim()) {
      errors.regex = t("regex.requiredField");
    } else {
      try {
        new RegExp(formData.regex);
      } catch (e) {
        errors.regex = t("regex.invalidRegex");
      }
    }

    if (!formData.description?.trim()) {
      errors.description = t("regex.requiredField");
    }
    console.log("Errores de formulario:", errors);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value, formSetter) => {
    formSetter((prev) => {
      const newState = { ...prev, [field]: value };

      if (field === "name" && formSetter === setNewPattern) {
        newState.id = generateId(value);
      }

      return newState;
    });

    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const testPattern = (regex, filename) => {
    if (!regex) {
      setFormErrors((prev) => ({
        ...prev,
        regex: t("regex.requiredField"),
      }));
      return;
    }

    try {
      setIsTesting(true);
      setTestResult(null);

      const regexObj = new RegExp(regex);
      const match = filename.match(regexObj);

      if (match) {
        if (match.length > 1) {
          setTestResult({
            success: true,
            value: match[1],
            allMatches: match.slice(1),
          });
        } else {
          setTestResult({
            success: true,
            value: "Patrón válido, pero sin grupos de captura (.*?)",
            fullMatch: match[0],
            allMatches: [],
          });
        }
      } else {
        setTestResult({
          success: false,
          value: null,
        });
      }
    } catch (e) {
      console.error("Error al probar el regex:", e);
      setFormErrors((prev) => ({
        ...prev,
        regex: t("regex.invalidRegex"),
      }));
      setTestResult(null);
    } finally {
      setIsTesting(false);
    }
  };

  const generateId = (name) => {
    if (!name) return "";
    const baseId = name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/gi, "_")
      .replace(/\s+/g, "_");

    return `${baseId}_regex`;
  };

  const handleAddPattern = async () => {
    if (!validateForm(newPattern)) return;

    try {
      setProcessingAction(true);

      const exists = patterns.some((p) => p.id === newPattern.id);
      if (exists) {
        setFormErrors((prev) => ({
          ...prev,
          id: t("regex.duplicateId"),
        }));
        setProcessingAction(false);
        return;
      }

      await createRegexPattern(newPattern);
      showSuccess(t("regex.createSuccess"));
      setShowAddModal(false);
      setNewPattern({
        id: "",
        regex: "",
        description: "",
        active: true,
        lastModified: new Date().toISOString(),
      });
      setTestResult(null);
      fetchPatterns();
    } catch (error) {
      showError(t("regex.createError"));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleUpdatePattern = async () => {
    if (!validateForm(selectedPattern)) return;

    try {
      setProcessingAction(true);
      console.log("Actualizando patrón:", selectedPattern);

      const patternToUpdate = {
        ...selectedPattern,
        lastModified: new Date().toISOString(),
      };

      console.log("Datos enviados a la API:", patternToUpdate);

      const result = await updateRegexPattern(
        selectedPattern.id,
        patternToUpdate
      );
      console.log("Resultado de actualización:", result);

      showSuccess(t("regex.updateSuccess"));
      setShowEditModal(false);
      setTestResult(null);
      fetchPatterns();
    } catch (error) {
      console.error("Error al actualizar el patrón:", error);
      showError(t("regex.updateError") + `: ${error.message || error}`);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDeletePattern = async () => {
    try {
      setProcessingAction(true);
      await deleteRegexPattern(selectedPattern.id);
      showSuccess(
        t("regex.deleteSuccess", {
          defaultValue: "Patrón eliminado exitosamente",
        })
      );
      setShowDeleteModal(false);
      fetchPatterns();
    } catch (error) {
      showError(t("regex.deleteError"));
    } finally {
      setProcessingAction(false);
    }
  };

  const testAllPatterns = () => {
    setIsTestingAllPatterns(true);
    setExtractionResults([]);

    const activePatterns = patterns.filter((pattern) => pattern.active);

    const results = activePatterns.map((pattern) => {
      try {
        const regexObj = new RegExp(pattern.regex);
        const match = testExtractionFilename.match(regexObj);

        return {
          id: pattern.id,
          name:
            pattern.name ||
            pattern.id.replace(/_regex$/i, "").replace(/_/g, " "),
          description: pattern.description,
          regex: pattern.regex,
          success: !!match,
          value: match && match.length > 1 ? match[1] : null,
          fullMatch: match ? match[0] : null,
        };
      } catch (e) {
        return {
          id: pattern.id,
          name:
            pattern.name ||
            pattern.id.replace(/_regex$/i, "").replace(/_/g, " "),
          description: pattern.description,
          regex: pattern.regex,
          success: false,
          error: e.message,
        };
      }
    });

    setExtractionResults(results);
    setIsTestingAllPatterns(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("regex.description", {
            defaultValue:
              "Gestiona expresiones regulares para extraer metadatos de archivos de audio",
          })}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="search"
                onClick={() => {
                  setTestExtractionFilename(
                    "llamada_SUP_Maria_BC_SantaFe_CT_OUT_GUID_12345.mp3"
                  );
                  setExtractionResults([]);
                  setShowTestExtractionModal(true);
                }}
              >
                {t("regex.testExtractionButton", {
                  defaultValue: "Probar extracción completa",
                })}
              </Button>
              <Button
                variant="primary"
                iconName="add-plus"
                onClick={() => {
                  setNewPattern({
                    id: "",
                    regex: "",
                    description: "",
                    active: true,
                    lastModified: new Date().toISOString(),
                  });
                  setFormErrors({});
                  setTestResult(null);
                  setShowAddModal(true);
                }}
              >
                {t("regex.createButton", {
                  defaultValue: "Crear patrón regex",
                })}
              </Button>
            </SpaceBetween>
          }
        >
          {t("regex.title")}
        </Header>
      }
    >
      <Container>
        {loading ? (
          <Box textAlign="center" padding={{ bottom: "l" }}>
            <Spinner size="large" />
          </Box>
        ) : (
          <Table
            items={paginatedItems}
            selectedItems={selectedItems}
            onSelectionChange={({ detail }) =>
              setSelectedItems(detail.selectedItems)
            }
            ariaLabels={{
              selectionGroupLabel: "Items selection",
              allItemsSelectionLabel: () => "select all",
              itemSelectionLabel: ({ selectedItems }, item) => item.id,
            }}
            columnDefinitions={[
              {
                id: "name",
                header: t("regex.patternName", {
                  defaultValue: "Nombre del patrón",
                }),
                cell: (item) => {
                  const displayName =
                    item.name ||
                    item.id.replace(/_regex$/i, "").replace(/_/g, " ");

                  return (
                    <Box display="flex" alignItems="center">
                      <Box>{displayName}</Box>
                      {item.isSystemPattern && (
                        <Box>
                          <Badge
                            color="blue"
                            style={{
                              padding: "5px 8px",
                              fontSize: "12px",
                              fontWeight: "normal",
                            }}
                          >
                            {t("regex.systemPattern", {
                              defaultValue: "Sistema",
                            })}
                          </Badge>
                        </Box>
                      )}
                    </Box>
                  );
                },
                sortingField: "name",
                isRowHeader: true,
              },
              {
                id: "id",
                header: t("regex.patternId"),
                cell: (item) => item.id,
                sortingField: "id",
              },
              {
                id: "regex",
                header: t("regex.patternRegex", {
                  defaultValue: "Expresión regular",
                }),
                cell: (item) => item.regex,
                sortingField: "regex",
              },
              {
                id: "description",
                header: t("regex.patternDescription", {
                  defaultValue: "Descripción",
                }),
                cell: (item) => item.description,
                sortingField: "description",
              },
              {
                id: "status",
                header: t("regex.patternStatus"),
                cell: (item) => (
                  <StatusIndicator type={item.active ? "success" : "stopped"}>
                    {item.active ? t("regex.active") : t("regex.inactive")}
                  </StatusIndicator>
                ),
                sortingField: "active",
              },
              {
                id: "lastModified",
                header: t("regex.lastModified", {
                  defaultValue: "Última modificación",
                }),
                cell: (item) => formatDate(item.lastModified),
                sortingField: "lastModified",
              },
              {
                id: "actions",
                header: t("regex.actions"),
                cell: (item) => (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      iconName="edit"
                      variant="icon"
                      disabled={item.isSystemPattern}
                      onClick={() => {
                        if (item.isSystemPattern) return;
                        setSelectedPattern(item);
                        setShowEditModal(true);
                      }}
                      ariaLabel={`Editar ${item.name || item.id}`}
                    />
                    <Button
                      iconName="remove"
                      variant="icon"
                      disabled={item.isSystemPattern}
                      onClick={() => {
                        if (item.isSystemPattern) return;
                        setSelectedPattern(item);
                        setShowDeleteModal(true);
                      }}
                      ariaLabel={`Eliminar ${item.name || item.id}`}
                    />
                  </SpaceBetween>
                ),
              },
            ]}
            filter={
              <TextFilter
                filteringPlaceholder={t("regex.searchPlaceholder", {
                  defaultValue: "Buscar patrones",
                })}
                filteringText={filteringText}
                onChange={({ detail }) =>
                  setFilteringText(detail.filteringText)
                }
                countText={
                  filteredItems.length > 0
                    ? `${filteredItems.length} ${
                        filteredItems.length === 1
                          ? t("regex.result")
                          : t("regex.results")
                      }`
                    : t("regex.noResults", {
                        defaultValue: "No hay resultados",
                      })
                }
              />
            }
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                onChange={({ detail }) =>
                  setCurrentPageIndex(detail.currentPageIndex)
                }
                pagesCount={pagesCount}
                ariaLabels={{
                  nextPageLabel: t("pagination.next", {
                    defaultValue: "Siguiente",
                  }),
                  previousPageLabel: t("pagination.previous", {
                    defaultValue: "Anterior",
                  }),
                  pageLabel: (pageNumber) => `${pageNumber} de ${pagesCount}`,
                }}
              />
            }
            empty={
              <Box
                margin={{ vertical: "xs" }}
                textAlign="center"
                color="inherit"
              >
                <SpaceBetween size="m">
                  <b>
                    {t("regex.noPatterns", {
                      defaultValue: "No hay patrones regex",
                    })}
                  </b>
                  <Button
                    onClick={() => {
                      setNewPattern({
                        id: "",
                        regex: "",
                        description: "",
                        active: true,
                        lastModified: new Date().toISOString(),
                      });
                      setFormErrors({});
                      setTestResult(null);
                      setShowAddModal(true);
                    }}
                  >
                    {t("regex.createButton", {
                      defaultValue: "Crear patrón regex",
                    })}
                  </Button>
                </SpaceBetween>
              </Box>
            }
          />
        )}
      </Container>

      <Modal
        visible={showAddModal}
        onDismiss={() => setShowAddModal(false)}
        header={t("regex.create")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowAddModal(false)} variant="link">
                {t("regex.cancel")}
              </Button>
              <Button
                onClick={handleAddPattern}
                variant="primary"
                loading={processingAction}
              >
                {t("regex.save")}
              </Button>
            </SpaceBetween>
          </Box>
        }
        size="large"
      >
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label={t("regex.patternName", {
              defaultValue: "Nombre del patrón",
            })}
            description={t("regex.nameHelperText", {
              defaultValue:
                "Nombre descriptivo para identificar fácilmente el patrón (ej: Supervisor, Banco)",
            })}
            errorText={formErrors.name}
          >
            <Input
              value={newPattern.name}
              onChange={({ detail }) =>
                handleInputChange("name", detail.value, setNewPattern)
              }
              placeholder="Supervisor"
            />
          </FormField>

          <FormField
            label={t("regex.patternId")}
            description={t("regex.idHelperText", {
              defaultValue:
                "Identificador único generado automáticamente a partir del nombre",
            })}
            errorText={formErrors.id}
          >
            <Input
              value={newPattern.id}
              disabled={true}
              placeholder="supervisor_regex"
            />
          </FormField>
          <FormField
            label={t("regex.patternRegex", {
              defaultValue: "Expresión regular",
            })}
            description={t("regex.regexHelperText", {
              defaultValue:
                "Expresión regular para extraer información (ej: _SUP_(.*?)_)",
            })}
            errorText={formErrors.regex}
          >
            <Input
              value={newPattern.regex}
              onChange={({ detail }) =>
                handleInputChange("regex", detail.value, setNewPattern)
              }
              placeholder="_SUP_(.*?)_"
            />
          </FormField>

          <FormField
            label={t("regex.patternDescription", {
              defaultValue: "Descripción",
            })}
            description={t("regex.descriptionHelperText", {
              defaultValue: "Descripción detallada del propósito del patrón",
            })}
            errorText={formErrors.description}
          >
            <Textarea
              value={newPattern.description}
              onChange={({ detail }) =>
                handleInputChange("description", detail.value, setNewPattern)
              }
              placeholder="Extrae el supervisor de la llamada"
            />
          </FormField>

          <FormField label={t("regex.patternStatus")}>
            <Toggle
              checked={newPattern.active}
              onChange={({ detail }) =>
                handleInputChange("active", detail.checked, setNewPattern)
              }
            >
              {newPattern.active ? t("regex.active") : t("regex.inactive")}
            </Toggle>
          </FormField>

          <FormField
            label={t("regex.testPattern")}
            description={t("regex.testHelperText", {
              defaultValue:
                "Ingresa un nombre de archivo de ejemplo para verificar si el patrón extrae correctamente la información",
            })}
          >
            <SpaceBetween direction="vertical" size="m">
              <Input
                value={testFilename}
                onChange={({ detail }) => setTestFilename(detail.value)}
                placeholder="llamada_SUP_Maria_BC_SantaFe_CT_OUT_GUID_12345.mp3"
              />

              <Button
                onClick={() => testPattern(newPattern.regex, testFilename)}
                loading={isTesting}
              >
                {t("regex.test")}
              </Button>

              {testResult !== null && (
                <div>
                  <Alert
                    type={testResult.success ? "success" : "warning"}
                    header={
                      testResult.success
                        ? t("regex.match", {
                            defaultValue: "Coincidencia encontrada",
                          })
                        : t("regex.noMatch", {
                            defaultValue: "Sin coincidencia",
                          })
                    }
                  >
                    {testResult.success ? (
                      <div>
                        {testResult.fullMatch ? (
                          <div>
                            <p>
                              <strong>
                                {t("regex.fullMatch", {
                                  defaultValue: "Coincidencia completa",
                                })}
                                :
                              </strong>{" "}
                              {testResult.fullMatch}
                            </p>
                            <p>
                              <small style={{ color: "orange" }}>
                                <strong>
                                  ⚠️{" "}
                                  {t("regex.noGroups", {
                                    defaultValue: "Advertencia",
                                  })}
                                  :{" "}
                                </strong>
                                {t("regex.noGroupsExplanation", {
                                  defaultValue:
                                    "Tu expresión regular no contiene grupos de captura. Para extraer información, debes incluir al menos un grupo usando paréntesis y un patrón como (.*?)",
                                })}
                              </small>
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p>
                              <strong>
                                {t("regex.resultValue", {
                                  defaultValue: "Valor extraído",
                                })}
                                :
                              </strong>{" "}
                              {testResult.value}
                            </p>
                            {testResult.allMatches &&
                              testResult.allMatches.length > 1 && (
                                <div>
                                  <p>
                                    <strong>
                                      {t("regex.additionalGroups", {
                                        defaultValue:
                                          "Grupos adicionales encontrados",
                                      })}
                                      :
                                    </strong>
                                  </p>
                                  <ul>
                                    {testResult.allMatches.map(
                                      (match, index) => (
                                        <li key={index}>
                                          Grupo {index + 1}: {match}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              )}
                            <p>
                              <small>
                                {t("regex.matchExplanation", {
                                  defaultValue:
                                    "El valor extraído corresponde al primer grupo de captura (.*?) en tu expresión regular.",
                                })}
                              </small>
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p>
                          {t("regex.noResultValue", {
                            defaultValue:
                              "No se encontró ningún valor que coincida con el patrón",
                          })}
                        </p>
                        <p>
                          <small>
                            {t("regex.checkPattern", {
                              defaultValue:
                                "Verifica que tu expresión regular tenga al menos un grupo de captura (.*?) y que coincida con alguna parte del nombre del archivo.",
                            })}
                          </small>
                        </p>
                      </div>
                    )}
                  </Alert>
                </div>
              )}
            </SpaceBetween>
          </FormField>

          <Box>
            <Header variant="h3">{t("regex.examples")}</Header>

            <SpaceBetween direction="vertical" size="s">
              <p>
                <strong>{t("regex.fileName")}</strong>:
                llamada_SUP_Maria_BC_SantaFe_CT_OUT_GUID_12345.mp3
              </p>

              <Table
                columnDefinitions={[
                  {
                    id: "pattern",
                    header: t("regex.patternExample", {
                      defaultValue: "Patrón",
                    }),
                    cell: (item) => item.pattern,
                  },
                  {
                    id: "explanation",
                    header: t("regex.explanation", {
                      defaultValue: "Explicación",
                    }),
                    cell: (item) => item.explanation,
                  },
                  {
                    id: "result",
                    header: t("regex.result"),
                    cell: (item) => item.result,
                  },
                ]}
                items={[
                  {
                    pattern: "_SUP_(.*?)_",
                    explanation: t("regex.supervisorExplanation", {
                      defaultValue:
                        'Captura texto entre "_SUP_" y el siguiente "_"',
                    }),
                    result: "Maria",
                  },
                  {
                    pattern: "_BC_(.*?)_",
                    explanation: t("regex.bankExplanation", {
                      defaultValue:
                        'Captura texto entre "_BC_" y el siguiente "_"',
                    }),
                    result: "SantaFe",
                  },
                  {
                    pattern: "_CT_(.*?)_",
                    explanation: t("regex.callTypeExplanation", {
                      defaultValue:
                        'Captura texto entre "_CT_" y el siguiente "_"',
                    }),
                    result: "OUT",
                  },
                  {
                    pattern: "_GUID_(.*?)\\.",
                    explanation: t("regex.guidExplanation", {
                      defaultValue:
                        'Captura texto entre "_GUID_" y el punto del archivo',
                    }),
                    result: "12345",
                  },
                ]}
                variant="embedded"
                stickyHeader={false}
                wrapLines={true}
              />

              <Alert type="info">
                <p>
                  <strong>
                    {t("regex.patternTips", {
                      defaultValue: "Consejos para crear patrones",
                    })}
                    :
                  </strong>
                </p>
                <ul>
                  <li>
                    {t("regex.tipGroups", {
                      defaultValue:
                        "Usa (.*?) para capturar cualquier texto entre dos delimitadores",
                    })}
                  </li>
                  <li>
                    {t("regex.tipEscape", {
                      defaultValue:
                        "Usa \\ para escapar caracteres especiales como punto (.)",
                    })}
                  </li>
                  <li>
                    {t("regex.tipTest", {
                      defaultValue:
                        "Prueba tu patrón con varios nombres de archivo para asegurar que funcione en todos los casos",
                    })}
                  </li>
                </ul>
              </Alert>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={showEditModal && selectedPattern !== null}
        onDismiss={() => setShowEditModal(false)}
        header={`${t("regex.edit")}: ${selectedPattern?.id}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowEditModal(false)} variant="link">
                {t("regex.cancel")}
              </Button>
              <Button
                onClick={handleUpdatePattern}
                variant="primary"
                loading={processingAction}
              >
                {t("regex.save")}
              </Button>
            </SpaceBetween>
          </Box>
        }
        size="large"
      >
        {selectedPattern && (
          <SpaceBetween direction="vertical" size="l">
            <FormField
              label={t("regex.patternName", {
                defaultValue: "Nombre del patrón",
              })}
              description={t("regex.nameHelperText", {
                defaultValue: "Nombre descriptivo para identificar el patrón",
              })}
              errorText={formErrors.name}
            >
              <Input
                value={
                  selectedPattern.name ||
                  selectedPattern.id.replace(/_regex$/i, "").replace(/_/g, " ")
                }
                onChange={({ detail }) => {
                  handleInputChange("name", detail.value, setSelectedPattern);
                }}
              />
            </FormField>
            <FormField
              label={t("regex.patternId")}
              description={t("regex.idHelperText", {
                defaultValue: "Identificador único para el patrón",
              })}
            >
              <Input value={selectedPattern.id} disabled={true} />
            </FormField>

            <FormField
              label={t("regex.patternRegex", {
                defaultValue: "Expresión regular",
              })}
              description={t("regex.regexHelperText", {
                defaultValue: "Expresión regular para extraer información",
              })}
              errorText={formErrors.regex}
            >
              <Input
                value={selectedPattern.regex}
                onChange={({ detail }) =>
                  handleInputChange("regex", detail.value, setSelectedPattern)
                }
              />
            </FormField>

            <FormField
              label={t("regex.patternDescription", {
                defaultValue: "Descripción",
              })}
              description={t("regex.descriptionHelperText", {
                defaultValue: "Descripción detallada del propósito del patrón",
              })}
              errorText={formErrors.description}
            >
              <Textarea
                value={selectedPattern.description}
                onChange={({ detail }) =>
                  handleInputChange(
                    "description",
                    detail.value,
                    setSelectedPattern
                  )
                }
              />
            </FormField>

            <FormField label={t("regex.patternStatus")}>
              <Toggle
                checked={selectedPattern.active}
                onChange={({ detail }) =>
                  handleInputChange(
                    "active",
                    detail.checked,
                    setSelectedPattern
                  )
                }
              >
                {selectedPattern.active
                  ? t("regex.active")
                  : t("regex.inactive")}
              </Toggle>
            </FormField>

            <FormField
              label={t("regex.testPattern")}
              description={t("regex.testHelperText", {
                defaultValue:
                  "Ingresa un nombre de archivo de ejemplo para verificar si el patrón extrae correctamente la información",
              })}
            >
              <SpaceBetween direction="vertical" size="m">
                <Input
                  value={testFilename}
                  onChange={({ detail }) => setTestFilename(detail.value)}
                  placeholder="llamada_SUP_Maria_BC_SantaFe_CT_OUT_GUID_12345.mp3"
                />

                <Button
                  onClick={() =>
                    testPattern(selectedPattern.regex, testFilename)
                  }
                  loading={isTesting}
                >
                  {t("regex.test")}
                </Button>

                {testResult !== null && (
                  <div>
                    <Alert
                      type={testResult.success ? "success" : "warning"}
                      header={
                        testResult.success
                          ? t("regex.match", {
                              defaultValue: "Coincidencia encontrada",
                            })
                          : t("regex.noMatch", {
                              defaultValue: "Sin coincidencia",
                            })
                      }
                    >
                      {testResult.success ? (
                        <div>
                          {testResult.fullMatch ? (
                            <div>
                              <p>
                                <strong>
                                  {t("regex.fullMatch", {
                                    defaultValue: "Coincidencia completa",
                                  })}
                                  :
                                </strong>{" "}
                                {testResult.fullMatch}
                              </p>
                              <p>
                                <small style={{ color: "orange" }}>
                                  <strong>
                                    ⚠️{" "}
                                    {t("regex.noGroups", {
                                      defaultValue: "Advertencia",
                                    })}
                                    :{" "}
                                  </strong>
                                  {t("regex.noGroupsExplanation", {
                                    defaultValue:
                                      "Tu expresión regular no contiene grupos de captura. Para extraer información, debes incluir al menos un grupo usando paréntesis y un patrón como (.*?)",
                                  })}
                                </small>
                              </p>
                            </div>
                          ) : (
                            <div>
                              <p>
                                <strong>
                                  {t("regex.resultValue", {
                                    defaultValue: "Valor extraído",
                                  })}
                                  :
                                </strong>{" "}
                                {testResult.value}
                              </p>
                              {testResult.allMatches &&
                                testResult.allMatches.length > 1 && (
                                  <div>
                                    <p>
                                      <strong>
                                        {t("regex.additionalGroups", {
                                          defaultValue:
                                            "Grupos adicionales encontrados",
                                        })}
                                        :
                                      </strong>
                                    </p>
                                    <ul>
                                      {testResult.allMatches.map(
                                        (match, index) => (
                                          <li key={index}>
                                            Grupo {index + 1}: {match}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                              <p>
                                <small>
                                  {t("regex.matchExplanation", {
                                    defaultValue:
                                      "El valor extraído corresponde al primer grupo de captura (.*?) en tu expresión regular.",
                                  })}
                                </small>
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <p>
                            {t("regex.noResultValue", {
                              defaultValue:
                                "No se encontró ningún valor que coincida con el patrón",
                            })}
                          </p>
                          <p>
                            <small>
                              {t("regex.checkPattern", {
                                defaultValue:
                                  "Verifica que tu expresión regular tenga al menos un grupo de captura (.*?) y que coincida con alguna parte del nombre del archivo.",
                              })}
                            </small>
                          </p>
                        </div>
                      )}
                    </Alert>
                  </div>
                )}
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        )}
      </Modal>

      <Modal
        visible={showDeleteModal && selectedPattern !== null}
        onDismiss={() => setShowDeleteModal(false)}
        header={t("regex.deleteTitle")}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowDeleteModal(false)} variant="link">
                {t("regex.cancel")}
              </Button>
              <Button
                onClick={handleDeletePattern}
                variant="primary"
                loading={processingAction}
              >
                {t("regex.delete")}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <p>
          {t("regex.deleteConfirm", {
            defaultValue: "¿Estás seguro de que deseas eliminar este patrón?",
            id: selectedPattern?.id,
          })}
        </p>
        <p>
          <strong>{selectedPattern?.id}</strong> -{" "}
          {selectedPattern?.description}
        </p>
      </Modal>

      <Modal
        visible={showTestExtractionModal}
        onDismiss={() => setShowTestExtractionModal(false)}
        header={t("regex.testExtraction", {
          defaultValue: "Probar extracción completa",
        })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={() => setShowTestExtractionModal(false)}
                variant="link"
              >
                {t("regex.close", { defaultValue: "Cerrar" })}
              </Button>
            </SpaceBetween>
          </Box>
        }
        size="large"
      >
        <SpaceBetween direction="vertical" size="l">
          <Alert type="info">
            <p>
              {t("regex.testExtractionInfo", {
                defaultValue:
                  "Esta herramienta te permite probar cómo se extraerían los datos de un nombre de archivo usando todos los patrones regex activos.",
              })}
            </p>
          </Alert>

          <FormField
            label={t("regex.testFilename")}
            description={t("regex.testFilenameDescription", {
              defaultValue:
                "Ingresa un nombre de archivo de ejemplo para ver qué información se extraería con los patrones activos",
            })}
          >
            <SpaceBetween direction="vertical" size="m">
              <Input
                value={testExtractionFilename}
                onChange={({ detail }) =>
                  setTestExtractionFilename(detail.value)
                }
                placeholder="llamada_SUP_Maria_BC_SantaFe_CT_OUT_GUID_12345.mp3"
              />

              <Button
                onClick={testAllPatterns}
                loading={isTestingAllPatterns}
                variant="primary"
              >
                {t("regex.runExtraction", {
                  defaultValue: "Ejecutar extracción",
                })}
              </Button>
            </SpaceBetween>
          </FormField>

          {extractionResults.length > 0 && (
            <Container
              header={
                <Header variant="h2">
                  {t("regex.extractionResults", {
                    defaultValue: "Resultados de la extracción",
                  })}
                </Header>
              }
            >
              <Table
                columnDefinitions={[
                  {
                    id: "name",
                    header: t("regex.patternName"),
                    cell: (item) => item.name,
                    width: 150,
                  },
                  {
                    id: "description",
                    header: t("regex.patternDescription"),
                    cell: (item) => item.description,
                    width: 200,
                  },
                  {
                    id: "result",
                    header: t("regex.extractedValue", {
                      defaultValue: "Valor extraído",
                    }),
                    cell: (item) =>
                      item.success ? (
                        item.value ? (
                          <div>
                            <StatusIndicator type="success">
                              {item.value}
                            </StatusIndicator>
                          </div>
                        ) : (
                          <div>
                            <StatusIndicator type="warning">
                              {t("regex.noGroupsInPattern", {
                                defaultValue: "Patrón sin grupos de captura",
                              })}
                            </StatusIndicator>
                          </div>
                        )
                      ) : (
                        <div>
                          <StatusIndicator type="error">
                            {item.error
                              ? t("regex.patternError", {
                                  defaultValue: "Error en el patrón",
                                })
                              : t("regex.noMatch", {
                                  defaultValue: "Sin coincidencia",
                                })}
                          </StatusIndicator>
                        </div>
                      ),
                  },
                ]}
                items={extractionResults}
                loadingText={t("regex.loading", {
                  defaultValue: "Cargando resultados",
                })}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>
                      {t("regex.noActivePatterns", {
                        defaultValue:
                          "No hay patrones regex activos para probar",
                      })}
                    </b>
                  </Box>
                }
                variant="embedded"
              />

              <Box padding={{ top: "l" }}>
                <Alert type="success">
                  <Header>
                    {t("regex.metadataPreview", {
                      defaultValue: "Vista previa de metadatos",
                    })}
                  </Header>
                  <p>
                    {t("regex.metadataPreviewDescription", {
                      defaultValue:
                        "Así es como se verían los metadatos extraídos:",
                    })}
                  </p>

                  <Container>
                    <SpaceBetween direction="vertical" size="xs">
                      {extractionResults
                        .filter((result) => result.success && result.value)
                        .map((result) => (
                          <div key={result.id}>
                            <strong>{result.name}:</strong> {result.value}
                          </div>
                        ))}
                    </SpaceBetween>
                  </Container>
                </Alert>
              </Box>
            </Container>
          )}

          {extractionResults.length === 0 && (
            <Box textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <div>
                  <b>
                    {t("regex.noResultsYet", {
                      defaultValue: "Aún no hay resultados",
                    })}
                  </b>
                  <p>
                    {t("regex.clickRunExtraction", {
                      defaultValue:
                        "Haz clic en 'Ejecutar extracción' para ver cómo se procesaría el archivo",
                    })}
                  </p>
                </div>
              </SpaceBetween>
            </Box>
          )}

          <Box>
            <Header variant="h3">
              {t("regex.howToUse", {
                defaultValue: "Cómo usar esta herramienta",
              })}
            </Header>

            <SpaceBetween direction="vertical" size="s">
              <p>
                {t("regex.howToUseDescription", {
                  defaultValue:
                    "Esta herramienta te ayuda a entender cómo se extraerían los metadatos de un nombre de archivo real:",
                })}
              </p>

              <ol>
                <li>
                  {t("regex.step1", {
                    defaultValue:
                      "Ingresa un nombre de archivo real como ejemplo",
                  })}
                </li>
                <li>
                  {t("regex.step2", {
                    defaultValue: "Haz clic en 'Ejecutar extracción'",
                  })}
                </li>
                <li>
                  {t("regex.step3", {
                    defaultValue:
                      "Revisa los valores extraídos por cada patrón activo",
                  })}
                </li>
                <li>
                  {t("regex.step4", {
                    defaultValue:
                      "Verifica la vista previa de metadatos para confirmar que la información es correcta",
                  })}
                </li>
              </ol>

              <p>
                <strong>{t("regex.tip", { defaultValue: "Consejo:" })}</strong>{" "}
                {t("regex.tipContent", {
                  defaultValue:
                    "Si algún patrón no extrae la información esperada, puedes editarlo desde la pantalla principal.",
                })}
              </p>
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
}

export default RegexPatterns;
